import { ReactNode, useState } from 'react';
import { useAuth } from '@/context/auth-provider';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Building2, Shield, AlertCircle, Mail, Clock, UserCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEnhancedOrganizationAccess } from '@/hooks/useEnhancedOrganizationAccess';
import { useUserProfile } from '@/hooks/useUserProfile';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface OrganizationAccessGateProps {
  children: ReactNode;
}

export const OrganizationAccessGate = ({
  children
}: OrganizationAccessGateProps) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { profileId } = useUserProfile();
  const [requesting, setRequesting] = useState(false);

  const {
    hasAccess,
    isLoading,
    accessType,
    pendingMembership,
    organization,
    membership,
    membershipSettings
  } = useEnhancedOrganizationAccess();

  const handleRequestMembership = async () => {
    if (!user || !profileId || !organization) return;
    
    try {
      setRequesting(true);
      
      const { error } = await supabase
        .from('pending_memberships')
        .insert({
          user_id: profileId,
          org_id: organization.id,
        });
      
      if (error) throw error;
      
      toast({
        title: "Request Submitted",
        description: "Your membership request has been submitted and is awaiting approval.",
      });
      
      // Refresh the page to show pending state
      window.location.reload();
    } catch (error) {
      console.error('Error requesting membership:', error);
      toast({
        title: "Error",
        description: "Failed to submit membership request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setRequesting(false);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/5 flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          <Card className="border-0 shadow-xl">
            <CardHeader className="text-center space-y-6">
              <Skeleton className="h-16 w-16 rounded-full mx-auto" />
              <div className="space-y-2">
                <Skeleton className="h-8 w-48 mx-auto" />
                <Skeleton className="h-4 w-64 mx-auto" />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Handle organization not found error
  if (!organization) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg border-red-200 dark:border-red-800">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/50 rounded-full flex items-center justify-center mb-4">
              <Building2 className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <CardTitle className="text-red-900 dark:text-red-100">Organization Not Found</CardTitle>
            <CardDescription className="text-red-700 dark:text-red-300">
              The organization you're trying to access doesn't exist or has been deactivated.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} className="w-full">
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Allow unauthenticated users to proceed (auth will handle redirects)
  if (accessType === 'unauthenticated') {
    return <>{children}</>;
  }

  // User has access - show the app
  if (hasAccess) {
    return <>{children}</>;
  }

  // Handle different access states
  if (accessType === 'pending') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg border-blue-200 dark:border-blue-800">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center mb-4">
              <Clock className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <CardTitle className="text-blue-900 dark:text-blue-100">Membership Pending</CardTitle>
            <CardDescription className="text-blue-700 dark:text-blue-300">
              Your membership request is awaiting approval
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Organization:</span>
                <Badge variant="outline">{organization.display_name || organization.name}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Request Status:</span>
                <Badge variant="secondary">Pending Approval</Badge>
              </div>
              {pendingMembership?.requested_at && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Requested:</span>
                  <span className="text-sm">{new Date(pendingMembership.requested_at).toLocaleDateString()}</span>
                </div>
              )}
            </div>
            
            <div className="pt-4 space-y-3">
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full"
              >
                Return to Home
              </Button>
              <Button
                onClick={signOut}
                variant="ghost"
                className="w-full"
              >
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (accessType === 'invite_only') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg border-purple-200 dark:border-purple-800">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-purple-100 dark:bg-purple-900/50 rounded-full flex items-center justify-center mb-4">
              <Mail className="w-8 h-8 text-purple-600 dark:text-purple-400" />
            </div>
            <CardTitle className="text-purple-900 dark:text-purple-100">Invitation Required</CardTitle>
            <CardDescription className="text-purple-700 dark:text-purple-300">
              This organization is invite-only. Please contact an administrator for access.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Organization:</span>
                <Badge variant="outline">{organization.display_name || organization.name}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Access Mode:</span>
                <Badge variant="secondary">Invite Only</Badge>
              </div>
            </div>
            
            <div className="pt-4 space-y-3">
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full"
              >
                Return to Home
              </Button>
              <Button
                onClick={signOut}
                variant="ghost"
                className="w-full"
              >
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Default denied state with request option
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg border-amber-200 dark:border-amber-800">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-amber-100 dark:bg-amber-900/50 rounded-full flex items-center justify-center mb-4">
            <UserCheck className="w-8 h-8 text-amber-600 dark:text-amber-400" />
          </div>
          <CardTitle className="text-amber-900 dark:text-amber-100">Organization Access</CardTitle>
          <CardDescription className="text-amber-700 dark:text-amber-300">
            Request access to join this organization
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Organization:</span>
              <Badge variant="outline">{organization.display_name || organization.name}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Access Mode:</span>
              <Badge variant="secondary">
                {membershipSettings?.membershipMode === 'open' ? 'Open' : 'Approval Required'}
              </Badge>
            </div>
          </div>

          {membershipSettings?.registrationMessage && (
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">
                {membershipSettings.registrationMessage}
              </p>
            </div>
          )}
          
          <div className="pt-4 space-y-3">
            <Button
              onClick={handleRequestMembership}
              disabled={requesting}
              className="w-full"
            >
              {requesting ? 'Requesting...' : 'Request Access'}
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="w-full"
            >
              Return to Home
            </Button>
            <Button
              onClick={signOut}
              variant="ghost"
              className="w-full"
            >
              Sign Out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};